/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import axios from 'axios';
import { useMeStore } from '@/services/UserService/useMeStore';

/**
 * If we need to change one of the values in the future, it's much easier to do that in one place (where the Enum is defined) than to find and replace every occurrence in your code.
 * Enums can make your code more maintainable and easier to refactor.It can also moved to a separate files.
 */

const embeddedReportTags = Object.freeze({
  advancedReport: 'advanced',
  customReport: 'custom',
  reportsHome: '(reportshome)'
});
export const useEmbeddedReportStore = defineStore('embeddedReportStore', {
  state: () => ({
    saving: false,
    alerts: {},
    keys: undefined,
    dashboards: [],
    customReportList: [],
    defaultDashboardId: '',
    deleting: false,
    highlightedDashboardId: null,
    shareableDashboards: [],
    deleteDashboardDialog: false,
    shareDashboardDialog: false,
    timeEntryDashboardId: '',
    deleted: false,
    resolvedProviders: {}
  }),
  actions: {
    async getEmbeddedReports() {
      try {
        const response = await axios({
          url: `/embeddedreporting`
        });
        this.keys = response.data;
      } catch (error) {
        this.alerts?.error(error.response.data.message);
      }
    },
    async getEmbeddedTimeEntryReports(params = {}) {
      try {
        const data = await axios({
          url: `/embeddedreporting`,
          params: params
        });
        return data;
      } catch (error) {
        this.alerts?.error(error.response?.data?.message);
      }
    },
    async listDashboard() {
      try {
        const response = await axios({
          url: `embeddedreporting/dashboards`
        });
        response.data.sort((a, b) => a.name.localeCompare(b.name));
        this.$patch({
          dashboards: response.data
        });
      } catch (error) {
        this.alerts?.error(error.response.data.message);
      }
    },
    async createDashboard() {
      try {
        const response = await axios({
          url: `/embeddedreporting/create`
        });
        this.alerts.success('Dashboard created successfully!');
        return response.data;
      } catch (error) {
        this.alerts?.error(error.response.data.message);
      }
    },
    async deleteDashboard(dashboardId) {
      try {
        await axios({
          url: `embeddedreporting/${dashboardId}`,
          method: 'DELETE'
        });
        this.deleted = true;
        this.alerts.success('Dashboard deleted successfully!');
      } catch (error) {
        this.alerts?.error(error.response.data.message);
      }
    },
    async copyDashboard(dashboardId) {
      try {
        const response = await axios({
          url: `embeddedreporting/copy/${dashboardId}`,
          method: 'GET'
        });
        this.alerts.success('Dashboard copied successfully!');
        return response.data;
      } catch (error) {
        this.alerts?.error(error.response.data.message);
      }
    },
    customReportGroups() {
      let customReport = [{
        isBeta: true,
        toggle: false,
        showReportList: !(this.keys && this.keys.canUseEditor) ? false : true,
        label: 'My Custom Reports',
        isEmbeddedCustomReport: true,
        options: this.getCustomReportOptions()
      }];
      return customReport;
    },
    sharedReportGroups() {
      let sharedReport = [{
        isBeta: true,
        toggle: false,
        showReportList: true,
        isEmbeddedSharedReport: true,
        label: 'Shared Reports',
        options: this.getSharedReportOptions()
      }];
      return sharedReport;
    },
    getCustomReportOptions() {
      const customReportGroups = this.dashboards.filter(item => item.tags?.includes(embeddedReportTags.customReport) || !item.tags?.includes(embeddedReportTags.advancedReport)).map(dataItem => {
        if (dataItem.isOwner) {
          return {
            id: dataItem.id,
            label: dataItem.name,
            name: dataItem.name,
            dateRangeOptions: false,
            embedded: true,
            embeddedCustomReport: true,
            isOwner: dataItem.isOwner,
            isVariant: dataItem.isVariant,
            canUseEditor: false,
            showReport: true,
            reportType: dataItem.id,
            tooltipData: dataItem.subTitle ? dataItem.subTitle : 'My Custom Report',
            createNewDashboardButton: true,
            dashboardId: dataItem.id,
            description: dataItem?.description
          };
        } else {
          return {};
        }
      });
      this.$patch({
        customReportList: customReportGroups
      });
      return customReportGroups;
    },
    getSharedReportOptions() {
      const sharedReportGroups = this.dashboards.filter(item => item.isShared).map(dataItem => {
        return {
          id: dataItem.id,
          name: dataItem.name,
          label: dataItem.name,
          dateRangeOptions: false,
          embedded: true,
          embeddedCustomReport: true,
          isOwner: dataItem.isOwner,
          owner: dataItem.owner,
          owner_id: dataItem.owner_id,
          isVariant: dataItem.isVariant,
          canUseEditor: false,
          showReport: true,
          reportType: dataItem.id,
          tooltipData: dataItem.subTitle ? dataItem.subTitle : 'My Custom Report',
          dashboardId: dataItem.id,
          description: dataItem?.description,
          isShared: dataItem.isShared
        };
      });
      return sharedReportGroups;
    },
    getDefaultDashboardId() {
      const defaultDashboard = this.getDashboardFromTag('reportshome');
      if (defaultDashboard) {
        this.defaultDashboardId = defaultDashboard.id;
        this.setHighlightedItem(defaultDashboard.id);
        return defaultDashboard.id;
      }
    },
    getDashboardFromTag(tag) {
      return this.dashboards.find(item => item.tags && (item.tags.includes(tag) || item.tags.includes(`(${tag})`) || item.locator === tag));
    },
    getTimeEntryDefaultDashboardId() {
      const timeEntryDefaultDashboard = this.getDashboardFromTag('reportstime');
      if (timeEntryDefaultDashboard) {
        this.timeEntryDashboardId = timeEntryDefaultDashboard.id;
        return timeEntryDefaultDashboard.id;
      }
    },
    async resolveDashboardByTag(tag) {
      const result = {
        provider: 'metabase',
        dashboardId: tag
      };
      this.resolvedProviders[tag] = result;
      return result;

      // if (this.resolvedProviders[tag]) {
      //   return this.resolvedProviders[tag];
      // }

      // try {
      //   const response = await axios({
      //     url: `insights/dashboards/${tag}/exists`,
      //   });
      //   if (response.data?.exists) {
      //     const result = { provider: 'metabase', dashboardId: tag };
      //     this.resolvedProviders[tag] = result;
      //     return result;
      //   }
      // } catch {
      //   // fall through to Luzmo
      // }

      // const meStore = useMeStore();
      // if (meStore.me?.features?.luzmoDead) return null;

      // if (!this.dashboards.length) {
      //   await this.listDashboard();
      // }
      // const dashboard = this.getDashboardFromTag(tag);
      // const result = { provider: 'luzmo', dashboardId: dashboard?.id || '' };
      // this.resolvedProviders[tag] = result;
      // return result;
    },
    setHighlightedItem(itemId) {
      this.highlightedDashboardId = itemId;
    },
    getDashboardLabelAndNameByDashboardId(dashboardId) {
      const dashboard = this.dashboards.find(item => item.id === dashboardId);
      return {
        name: dashboard?.name,
        type: dashboard?.isAdvanced ? 'Advanced Report' : 'My Custom Report',
        owner: dashboard?.owner
      };
    }
  }
});